/**
 * Centralized Bundle Manager
 * Handles all bundle cart operations to ensure consistency between cart drawer,
 * bundle drawer, and Shopify Functions discount application.
 */
const DEBUG = false; // Set to true for development debugging

if (!window.BundleManager) {
  window.BundleManager = class BundleManager {
  constructor() {
    this.isProcessing = false;
    this.setupCartRemovalInterceptors();
    this.validateCartOnLoad(); // Check for and cleanup orphaned addons on page load
  }

  /**
   * Setup event listeners to intercept cart removal clicks
   * This avoids modifying the theme's core cart.js file
   */
  setupCartRemovalInterceptors() {
    document.addEventListener('click', (e) => {
      // Check if clicked element is a cart remove button
      const removeButton = e.target.closest('a[is="cart-remove-button"]');
      if (!removeButton) return;

      // Logging removed
      // Logging removed

      // IMMEDIATELY prevent default to stop theme handler
      // We'll check async and manually trigger theme handler if needed
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Prevent any removals while processing (avoid race conditions)
      if (this.isProcessing) {
        console.warn('[BundleManager] Operation in progress, blocking click');
        return;
      }

      const lineIndex = removeButton.dataset.index;

      // Check async if this needs special handling
      this.checkIfNeedsSpecialHandling(lineIndex).then(needsSpecialHandling => {
        // Logging removed

        if (needsSpecialHandling) {
          // Logging removed
          this.handleCartItemRemoval(lineIndex, removeButton);
        } else {
          // Logging removed
          // Manually trigger the theme's removal logic
          const cartItems = removeButton.closest('cart-items');
          if (cartItems) {
            cartItems.updateQuantity(removeButton.dataset.index, 0);
          }
        }
      });
    }, true); // Use capture phase to intercept before theme handlers
  }

  /**
   * Check if item needs special bundle handling
   */
  async checkIfNeedsSpecialHandling(lineIndex) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const item = cart.items[lineIndex - 1];

      if (!item) return false;

      const isParentWithAddons = item.properties?.productLinkId && !item.properties?._bundleComponent;
      const isBundleAddon = item.properties?._bundleComponent === 'true';

      // Check if parent has addons
      if (isParentWithAddons) {
        const productLinkId = item.properties.productLinkId;
        const addons = cart.items.filter(cartItem =>
          cartItem.properties?.productLinkId === productLinkId &&
          cartItem.properties?._bundleComponent === 'true'
        );
        return addons.length > 0; // Only intercept if there are addons
      }

      return isBundleAddon; // Always intercept addon removal
    } catch (error) {
      console.error('[BundleManager] Error checking item:', error);
      return false;
    }
  }

  /**
   * Handle removal of any cart item - checks if it's parent or addon
   */
  async handleCartItemRemoval(lineIndex, removeButton) {
    if (this.isProcessing) {
      console.warn('[BundleManager] Already processing, please wait');
      return;
    }

    this.isProcessing = true;

    // Show loading state
    const cartItems = removeButton.closest('cart-items');
    if (cartItems) {
      cartItems.enableLoading(lineIndex);
    }

    try {
      const cart = await fetch('/cart.js').then(r => r.json());

      // Logging removed

      const item = cart.items[lineIndex - 1];

      if (!item) {
        console.error('[BundleManager] Item not found at index:', lineIndex);
        this.isProcessing = false;
        return;
      }

      // Logging removed

      const isParentProduct = item.properties?.productLinkId && !item.properties?._bundleComponent;
      const isBundleAddon = item.properties?._bundleComponent === 'true';

      // CASE 1: Parent product with addons
      if (isParentProduct) {
        await this.removeParentWithAddons(item, lineIndex, cart);
        return;
      }

      // CASE 2: Bundle addon
      if (isBundleAddon) {
        // Logging removed

        try {
          // Get the productLinkId to find other items in the bundle
          const productLinkId = item.properties?.productLinkId;

          // Collect sections that need to be updated
          let sectionsToBundle = [];
          document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          }));

          // Logging removed

          // Remove the addon using line index (not key)
          const body = JSON.stringify({
            line: lineIndex,
            quantity: 0,
            sections: sectionsToBundle
          });

          // Logging removed

          const response = await fetch(window.theme.routes.cart_change_url, {
            ...window.theme.utils.fetchConfig(),
            ...{ body }
          });

          // Logging removed

          if (!response.ok) {
            const errorText = await response.text();
            console.error('[BundleManager] Failed to remove addon - status:', response.status);
            console.error('[BundleManager] Error response:', errorText);
            this.isProcessing = false;
            return;
          }

        let updatedCart = await response.json();

        // Logging removed
        // Logging removed

        // Logging removed

        // Find remaining bundle items with same productLinkId
        const remainingBundleItems = updatedCart.items.filter(cartItem =>
          cartItem.properties?.productLinkId === productLinkId &&
          cartItem.properties?._bundleComponent === 'true'
        );

        // Logging removed
        // Logging removed

        // If there are remaining items, recalculate tier
        if (remainingBundleItems.length > 0) {
          // Logging removed

          // Show loading overlay
          this.showRecalculatingOverlay();

          // Recalculate tier (removes and re-adds items with new tier)
          await this.updateBundleTier(remainingBundleItems, productLinkId);

          // Hide loading overlay
          this.hideRecalculatingOverlay();

          // After tier update, fetch the final cart state with sections for UI
          // Use a dummy change request with quantity 0 to get sections
          sectionsToBundle = [];
          document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          }));

          // Get the updated cart by fetching with sections
          const cart = await fetch('/cart.js').then(r => r.json());

          // Make a no-op change to get sections in response
          if (cart.items.length > 0) {
            const finalBody = JSON.stringify({
              line: 1, // Use first item's line number
              quantity: cart.items[0].quantity, // Keep same quantity (no-op)
              sections: sectionsToBundle
            });

            // Logging removed

            const finalResponse = await fetch(window.theme.routes.cart_change_url, {
              ...window.theme.utils.fetchConfig(),
              ...{ body: finalBody }
            });

            if (finalResponse.ok) {
              updatedCart = await finalResponse.json();
              // Logging removed
            } else {
              console.error('[BundleManager] Failed to fetch final cart state:', finalResponse.status);
            }
          }
        }

          // Trigger theme's cart update with proper event parameters
          // IMPORTANT: Only trigger this AFTER tier recalculation is complete
          // to avoid line number shifts during the process
          if (window.theme && window.theme.pubsub) {
            // Logging removed
            window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              cart: updatedCart,
              line: lineIndex,
              name: 'minus',
              source: 'bundle-manager'
            });
          }

        } catch (addonError) {
          console.error('[BundleManager] Error removing addon:', addonError);
          // Make sure overlay is hidden on error
          this.hideRecalculatingOverlay();
        } finally {
          this.isProcessing = false;
        }
        return;
      }

    } catch (error) {
      console.error('[BundleManager] Error handling item removal:', error);
      // Make sure overlay is hidden on error
      this.hideRecalculatingOverlay();
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Remove parent product and all its addons
   */
  async removeParentWithAddons(item, lineIndex, cart) {
    const productLinkId = item.properties.productLinkId;
    const addons = cart.items.filter(cartItem =>
      cartItem.properties?.productLinkId === productLinkId &&
      cartItem.properties?._bundleComponent === 'true'
    );

    // Show confirmation dialog
    const confirmed = await this.showParentRemovalDialog(item.product_title, addons.length);

    if (!confirmed) {
      this.isProcessing = false;
      return;
    }

    // Logging removed

    // Collect sections that need to be updated (same as theme does)
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    // Logging removed

    // Collect all items to remove (addons + parent)
    const itemsToRemove = [...addons, item];

    // Build atomic updates object for bulk removal
    const updates = {};
    itemsToRemove.forEach(itemToRemove => {
      updates[itemToRemove.key] = 0;
    });

    console.log('[BundleManager] Removing', itemsToRemove.length, 'items atomically');

    // Single atomic request using /cart/update.js with sections for UI refresh
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        sections: sectionsToBundle.join(',')
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[BundleManager] Atomic removal failed:', errorText);
      await this.showRemovalErrorDialog(item.product_title);
      this.isProcessing = false;
      return;
    }

    const updatedCart = await response.json();
    console.log('[BundleManager] ✓ Atomic removal successful');

    // Verify all items were removed
    const orphansExist = updatedCart.items.some(cartItem =>
      cartItem.properties?.productLinkId === productLinkId
    );

    if (orphansExist) {
      console.warn('[BundleManager] ⚠️  Orphaned items detected after removal, scheduling cleanup');
      // Schedule cleanup after a short delay
      setTimeout(() => this.cleanupOrphanedAddons(productLinkId), 2000);
    }

    // Trigger theme's cart update with proper event parameters
    if (window.theme && window.theme.pubsub) {
      window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        cart: updatedCart,
        line: lineIndex,
        name: 'minus',
        source: 'bundle-manager'
      });
    }

    this.isProcessing = false;
  }

  /**
   * Show confirmation dialog for parent removal
   */
  async showParentRemovalDialog(productTitle, addonCount) {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 1rem;';

      const dialog = document.createElement('div');
      dialog.style.cssText = 'background: white; border-radius: 0.5rem; padding: 1.5rem; max-width: 28rem; width: 100%; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);';

      dialog.innerHTML = `
        <div style="margin-bottom: 1rem;">
          <h3 style="font-size: 1.125rem; font-weight: 700; margin-bottom: 0.5rem;">Remove ${productTitle}?</h3>
          <p style="font-size: 0.875rem; color: #6b7280;">
            This product has <strong>${addonCount} special touch${addonCount > 1 ? 'es' : ''}</strong> added to it.
            Removing this product will also remove all associated add-ons from your cart.
          </p>
        </div>
        <div style="display: flex; gap: 0.75rem; justify-content: flex-end;">
          <button class="button button--secondary" style="padding: 0.5rem 1rem;" data-action="cancel">
            <span class="btn-text">Cancel</span>
          </button>
          <button class="button button--primary" style="padding: 0.5rem 1rem;" data-action="confirm">
            <span class="btn-text">Remove All</span>
          </button>
        </div>
      `;

      overlay.appendChild(dialog);
      document.body.appendChild(overlay);

      dialog.querySelector('[data-action="cancel"]').addEventListener('click', () => {
        document.body.removeChild(overlay);
        resolve(false);
      });

      dialog.querySelector('[data-action="confirm"]').addEventListener('click', () => {
        document.body.removeChild(overlay);
        resolve(true);
      });

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          document.body.removeChild(overlay);
          resolve(false);
        }
      });
    });
  }

  /**
   * Show error dialog when removal fails
   */
  async showRemovalErrorDialog(productTitle) {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 1rem;';

      const dialog = document.createElement('div');
      dialog.style.cssText = 'background: white; border-radius: 0.5rem; padding: 1.5rem; max-width: 28rem; width: 100%; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);';

      dialog.innerHTML = `
        <div style="margin-bottom: 1rem;">
          <h3 style="font-size: 1.125rem; font-weight: 700; margin-bottom: 0.5rem; color: #dc2626;">Failed to Remove ${productTitle}</h3>
          <p style="font-size: 0.875rem; color: #6b7280;">
            There was an error removing this product and its add-ons from your cart.
            Please refresh the page and try again.
          </p>
        </div>
        <div style="display: flex; gap: 0.75rem; justify-content: flex-end;">
          <button class="button button--secondary" style="padding: 0.5rem 1rem;" data-action="close">
            <span class="btn-text">Close</span>
          </button>
          <button class="button button--primary" style="padding: 0.5rem 1rem;" data-action="refresh">
            <span class="btn-text">Refresh Page</span>
          </button>
        </div>
      `;

      overlay.appendChild(dialog);
      document.body.appendChild(overlay);

      dialog.querySelector('[data-action="close"]').addEventListener('click', () => {
        document.body.removeChild(overlay);
        resolve();
      });

      dialog.querySelector('[data-action="refresh"]').addEventListener('click', () => {
        window.location.reload();
      });

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          document.body.removeChild(overlay);
          resolve();
        }
      });
    });
  }


  /**
   * Get all bundle items from cart
   */
  async getBundleItemsFromCart() {
    const cart = await fetch('/cart.js').then(r => r.json());
    return cart.items.filter(item =>
      item.properties && item.properties._bundleComponent === 'true'
    );
  }

  /**
   * Calculate tier based on addon selections
   * SPEND-BASED DISCOUNTS:
   * £50+ in bundle → 15% off (value-bundle)
   * £150+ in bundle → 25% off (complete-bundle)
   * £250+ in bundle → 30% off (elite-bundle)
   */
  calculateTier(addons) {
    if (!addons || addons.length === 0) {
      return { current: 'basic', discount: '0%' };
    }

    // Calculate total price from addons
    const totalPrice = addons.reduce((sum, addon) => sum + (addon.price || 0), 0);

    let currentTier = 'basic';
    let discount = '0%';

    // Pure spend-based tier calculation
    if (totalPrice >= 250) {
      // £250+ = 30% off
      currentTier = 'elite-bundle';
      discount = '30%';
    } else if (totalPrice >= 150) {
      // £150-£249 = 25% off
      currentTier = 'complete-bundle';
      discount = '25%';
    } else if (totalPrice >= 50) {
      // £50-£149 = 15% off
      currentTier = 'value-bundle';
      discount = '15%';
    } else {
      // Under £50 = 0% off
      currentTier = 'basic';
      discount = '0%';
    }

    // Logging removed

    return { current: currentTier, discount };
  }

  /**
   * Remove a single bundle item and recalculate tier for remaining items
   * This is called from cart drawer when user clicks remove on a bundle item
   */
  async removeBundleItem(cartItemKey) {
    if (this.isProcessing) {
      console.warn('[BundleManager] Already processing, please wait');
      return;
    }

    this.isProcessing = true;
    // Logging removed

    try {
      // Get current cart
      const cart = await fetch('/cart.js').then(r => r.json());

      // Find the item being removed
      const itemToRemove = cart.items.find(item => item.key === cartItemKey);
      if (!itemToRemove) {
        console.error('[BundleManager] Item not found:', cartItemKey);
        return;
      }

      // Get all bundle items with same productLinkId
      const productLinkId = itemToRemove.properties?.productLinkId;
      if (!productLinkId) {
        // Not a bundle item, just remove normally
        await fetch(window.theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: cartItemKey, quantity: 0 })
        });
        this.isProcessing = false;
        return;
      }

      const allBundleItems = cart.items.filter(item =>
        item.properties?.productLinkId === productLinkId &&
        item.properties?._bundleComponent === 'true'
      );

      // Logging removed

      // Remove the item
      await fetch(window.theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: cartItemKey, quantity: 0 })
      });

      // Get remaining items (excluding the one we just removed)
      const remainingItems = allBundleItems.filter(item => item.key !== cartItemKey);

      if (remainingItems.length === 0) {
        // Logging removed
        this.isProcessing = false;
        return;
      }

      // Recalculate tier for remaining items
      await this.updateBundleTier(remainingItems, productLinkId);

    } catch (error) {
      console.error('[BundleManager] Error removing bundle item:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Update tier for all items in a bundle
   * Removes and re-adds items to trigger Shopify Functions
   */
  async updateBundleTier(bundleItems, productLinkId) {
    if (bundleItems.length === 0) {
      // Logging removed
      return;
    }

    // Logging removed
    // Logging removed
    // Logging removed

    // Map to addon format for tier calculation
    // IMPORTANT: item.price from cart is in CENTS, need to convert to POUNDS
    const addonsForTier = bundleItems.map(item => ({
      tierValue: this.getTierValueFromItem(item),
      price: item.price / 100,  // Convert cents to pounds
      variantId: item.variant_id
    }));

    // Logging removed

    // Calculate new tier
    const tierInfo = this.calculateTier(addonsForTier);
    const newItemCount = bundleItems.length;

    // Logging removed
    // Logging removed

    // Backup all items with their properties
    const backedUpItems = bundleItems.map(item => ({
      variantId: item.variant_id,
      quantity: item.quantity,
      properties: { ...item.properties },
      key: item.key,
      title: item.title
    }));

    // Logging removed

    // Remove ALL bundle items in one batch
    const updates = {};
    bundleItems.forEach(item => {
      // Logging removed
      updates[item.key] = 0;
    });

    // Logging removed
    // Logging removed

    const removeResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });

    // Logging removed

    if (!removeResponse.ok) {
      const errorText = await removeResponse.text();
      console.error('[BundleManager] Failed to remove items for tier update:', errorText);
      throw new Error('Failed to remove items for tier update');
    }

    const removeResult = await removeResponse.json();
    // Logging removed
    // Logging removed

    // Re-add ALL items with updated tier
    for (const backedUp of backedUpItems) {
      const properties = {
        ...backedUp.properties,
        productLinkId: productLinkId,
        _bundleTier: tierInfo.current,
        _bundleItemCount: String(newItemCount),
        _bundleComponent: 'true',
        _bundle: 'custom'
      };

      // Logging removed

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: backedUp.variantId,
            quantity: backedUp.quantity,
            properties: properties
          }]
        })
      });

      if (!addResponse.ok) {
        const errorText = await addResponse.text();
        console.error('[BundleManager] Failed to re-add item:', errorText);
      } else {
        const addResult = await addResponse.json();
        // Logging removed
      }
    }

    // Logging removed
  }

  /**
   * Get tier value from a cart item
   * Estimates based on price if not available
   */
  getTierValueFromItem(item) {
    // Try to get from properties first
    if (item.properties?._tierValue) {
      return parseFloat(item.properties._tierValue);
    }

    // Estimate: assume £10 = 20 tierValue (default addon pricing)
    const priceInPounds = item.price / 100;
    return Math.round((priceInPounds / 10) * 20);
  }

  /**
   * Build an error whose message is safe to show to the customer
   */
  createUserError(userMessage) {
    const error = new Error(userMessage);
    error.userMessage = userMessage;
    return error;
  }

  /**
   * Best-effort undo of a failed sync: removes the bundle lines added by the failed attempt and
   * puts back the lines that were removed before it, so a failure never costs the customer
   * the bundle they already had in their cart.
   */
  async rollbackBundleSync(productLinkId, removedItems) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const partialItems = cart.items.filter(item =>
        item.properties?.productLinkId === productLinkId &&
        item.properties?._bundleComponent === 'true'
      );

      if (partialItems.length > 0) {
        const updates = {};
        partialItems.forEach(item => {
          updates[item.key] = 0;
        });
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
      }

      for (const item of removedItems) {
        const restoreResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: item.variant_id,
              quantity: item.quantity,
              properties: item.properties || {}
            }]
          })
        });

        if (!restoreResponse.ok) {
          console.error('[BundleManager] Rollback could not restore item:', item.product_title, restoreResponse.status);
        }
      }
    } catch (rollbackError) {
      console.error('[BundleManager] Rollback after failed bundle sync did not complete:', rollbackError);
    }
  }

  /**
   * Sync bundle to cart
   * Called from bundle drawer when adding/updating bundle
   */
  async syncBundleToCart(selectedAddons, productLinkId) {
    if (this.isProcessing) {
      console.warn('[BundleManager] Already processing, please wait');
      throw this.createUserError('Your bundle is still being added. Please wait a moment.');
    }

    // PRE-FLIGHT: never touch the cart when a selected option is missing or sold out
    const incompleteAddon = selectedAddons.find(addon => !addon.variantId);
    if (incompleteAddon) {
      throw this.createUserError(
        `We could not add ${incompleteAddon.name || incompleteAddon.handle}. Please remove it and select it again.`
      );
    }

    const blockedAddon = selectedAddons.find(addon => {
      const chosenVariant = Array.isArray(addon.variants)
        ? addon.variants.find(v => v.id == addon.variantId)
        : null;
      return chosenVariant ? chosenVariant.available === false : false;
    });
    if (blockedAddon) {
      throw this.createUserError(
        `${blockedAddon.name || blockedAddon.handle} is sold out in the option you picked. Please choose a different option.`
      );
    }

    this.isProcessing = true;
    let syncPhase = 'removing';
    let removedItems = [];
    // Logging removed
    // Logging removed
    // Logging removed

    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      // Logging removed

      // Get existing bundle items with same productLinkId
      const existingBundleItems = cart.items.filter(item =>
        item.properties?.productLinkId === productLinkId &&
        item.properties?._bundleComponent === 'true'
      );

      // Logging removed
      // Logging removed

      // Calculate tier for selected addons
      const tierInfo = this.calculateTier(selectedAddons);
      const totalItemCount = selectedAddons.length;

      // Logging removed

      // STEP 1: Remove ALL existing bundle items
      if (existingBundleItems.length > 0) {
        // Logging removed

        // Build updates object to remove all items in one request
        const updates = {};
        existingBundleItems.forEach(item => {
          // Use variant_id:properties_hash format for the key
          updates[item.key] = 0;
        });

        // Logging removed

        // Use /cart/update.js to remove all at once
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });

        if (!response.ok) {
          console.error('[BundleManager] Failed to remove items:', response.status);
          throw new Error('Failed to remove existing bundle items');
        }

        const updatedCart = await response.json();
        removedItems = existingBundleItems;
        // Logging removed
        // Logging removed
      } else {
        // Logging removed
      }

      // STEP 2: Add ALL selected items
      syncPhase = 'adding';
      // Logging removed

      for (const addon of selectedAddons) {
        // Logging removed

        const properties = {
          productLinkId: productLinkId,
          _bundle: 'custom',
          _bundleTier: tierInfo.current,
          _bundleComponent: 'true',
          _bundleItemCount: String(totalItemCount),
          _tierValue: String(addon.tierValue || 0)
        };

        // Mark free items (alwaysIncluded) so discount function can apply 100% discount
        if (addon.alwaysIncluded) {
          properties['_alwaysIncluded'] = 'true';
        }

        // Add configuration properties if present
        if (addon.configuration) {
          if (addon.handle === 'number-plate-addon') {
            properties['Customised Number Plate'] = addon.configuration.front;
            properties['Customised Number Plate Back'] = addon.configuration.back;
            properties['Design'] = addon.configuration.design;
          } else if (addon.handle === 'personalised-letter') {
            properties['Theme'] = addon.configuration.selectedTheme;
            properties['Template'] = addon.configuration.selectedTemplate;
            properties['Background'] = addon.configuration.selectedBackground;
            properties['Child Name'] = addon.configuration.formData.childName;
            properties['Town/City'] = addon.configuration.formData.townCity;

            if (addon.configuration.selectedAge) {
              properties['Age'] = addon.configuration.selectedAge;
            }
            if (addon.configuration.formData.achievement) {
              properties['Achievement'] = addon.configuration.formData.achievement;
            }
            if (addon.configuration.formData.gifts) {
              properties['Gift List'] = addon.configuration.formData.gifts;
            }
            if (addon.configuration.formData.specialMessage) {
              properties['Special Message'] = addon.configuration.formData.specialMessage;
            }
          } else if (addon.handle === 'personalised-animated-video') {
            properties['Child Name'] = addon.configuration.childName;
            properties['Customer Email'] = addon.configuration.email;
            properties['Photo Uploaded'] = addon.configuration.photoData ? 'Yes' : 'No';

            // Store S3 URL and metadata (uploaded to secure cloud storage)
            // Photo is compressed (800px, JPEG 70%) and stored in S3: santa-videos/pending/{uuid}.jpg
            if (addon.configuration.photoData) {
              properties['_photoUrl'] = addon.configuration.photoData; // S3 public URL
              if (addon.configuration.photoS3Key) {
                properties['_photoS3Key'] = addon.configuration.photoS3Key; // S3 key for backend access
              }
            }

            // Add main product info for video generation
            // Product info will be fetched from cart after items are added (see below)
            properties['_needsProductInfo'] = 'true';
          }
        }

        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: addon.variantId,
              quantity: 1,
              properties: properties
            }]
          })
        });

        if (!addResponse.ok) {
          const errorBody = await addResponse.json().catch(() => null);
          console.error('[BundleManager] Failed to add item:', addon.handle, addResponse.status, errorBody);
          throw this.createUserError(
            errorBody?.description || `We could not add ${addon.name || addon.handle} to your cart. Please try again.`
          );
        }
      }

      // STEP 3: Update main product to link it to this bundle
      syncPhase = 'linking';
      // Extract productId from productLinkId (format: "productId-timestamp")
      const productId = productLinkId.split('-')[0];
      // Logging removed

      // Get current cart to find main product
      const currentCart = await fetch('/cart.js').then(r => r.json());

      // Find main product that either has this productLinkId OR doesn't have one yet
      console.log('[BundleManager] Looking for main product with productId:', productId, 'productLinkId:', productLinkId);
      console.log('[BundleManager] Available cart items:', currentCart.items.map(item => ({
        product_id: item.product_id,
        title: item.product_title,
        _bundleComponent: item.properties?._bundleComponent,
        productLinkId: item.properties?.productLinkId
      })));

      // First try to find by exact productLinkId match (for editing existing bundles)
      let mainProduct = currentCart.items.find(item =>
        item.properties?.productLinkId === productLinkId &&
        !item.properties?._bundleComponent
      );

      // If not found, try by product_id (for new bundles)
      if (!mainProduct) {
        mainProduct = currentCart.items.find(item =>
          item.product_id == productId &&
          !item.properties?._bundleComponent &&
          !item.properties?.productLinkId
        );
      }

      console.log('[BundleManager] Found main product:', mainProduct ? mainProduct.product_title : 'NOT FOUND');

      if (mainProduct) {
        // Logging removed

        // Update main product to add productLinkId (use key instead of line index for reliability)
        const updateMainResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: mainProduct.key,
            properties: {
              ...mainProduct.properties,
              productLinkId: productLinkId
            }
          })
        });

        if (!updateMainResponse.ok) {
          console.error('[BundleManager] Failed to update main product:', updateMainResponse.status);
          // Non-fatal - bundle items are still added, just not linked
        }

        // Logging removed

        // STEP 4: Update video items with main product info for video generation
        // Get updated cart to find video items that need product info
        console.log('[BundleManager] Checking for video items that need product info...');
        const updatedCartForVideo = await fetch('/cart.js').then(r => r.json());

        // DEBUG: Log all items to see what we have
        console.log('[BundleManager] Cart items after adding bundle:', updatedCartForVideo.items.map(item => ({
          title: item.title,
          productLinkId: item.properties?.productLinkId,
          _bundleComponent: item.properties?._bundleComponent,
          _needsProductInfo: item.properties?._needsProductInfo
        })));

        const videoItem = updatedCartForVideo.items.find(item =>
          item.properties?.productLinkId === productLinkId &&
          item.properties?._bundleComponent === 'true' &&
          item.properties?._needsProductInfo === 'true'
        );

        console.log('[BundleManager] Found video item:', videoItem ? 'Yes' : 'No');
        console.log('[BundleManager] Main product available:', mainProduct ? 'Yes' : 'No');

        if (videoItem && mainProduct) {
          // DEBUG: Log main product data for video generation
          console.log('[BundleManager] Main product data for video:', {
            product_title: mainProduct.product_title,
            sku: mainProduct.sku,
            variant_id: mainProduct.variant_id,
            featured_image: mainProduct.featured_image,
            image: mainProduct.image,
            product_description: mainProduct.product_description?.substring(0, 500)
          });

          // Extract product dimensions from description
          let dimensions = null;
          if (mainProduct.product_description) {
            const dimMatch = mainProduct.product_description.match(/Product Dimensions\s*:\s*([^<\n]+)/i);
            if (dimMatch) {
              const dimString = dimMatch[1];
              const cleaned = dimString.replace(/cm/gi, '').trim();
              const parts = cleaned.split(/\s*[xX×]\s*/);
              if (parts.length >= 3) {
                dimensions = {
                  length: parseFloat(parts[0]) || 0,
                  width: parseFloat(parts[1]) || 0,
                  height: parseFloat(parts[2]) || 0,
                  raw: dimString.trim()
                };
              }
            }
          }

          // DEBUG: Log extracted dimensions
          console.log('[BundleManager] Extracted dimensions:', dimensions);

          // Build updated properties with product info
          const videoProperties = {
            ...videoItem.properties,
            '_productName': mainProduct.product_title,
            '_productSku': mainProduct.sku || '',
            '_productVariantId': String(mainProduct.variant_id),
            '_productImageUrl': mainProduct.featured_image?.url || mainProduct.image || ''
          };

          // Remove the flag
          delete videoProperties['_needsProductInfo'];

          // Add dimensions if found
          if (dimensions) {
            videoProperties['_productDimensions'] = dimensions.raw;
            videoProperties['_productLength'] = String(dimensions.length);
            videoProperties['_productWidth'] = String(dimensions.width);
            videoProperties['_productHeight'] = String(dimensions.height);
          }

          // DEBUG: Log final video properties being set
          console.log('[BundleManager] Final video properties:', videoProperties);

          // Update the video item with product info
          const updateVideoResponse = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: videoItem.key,
              properties: videoProperties
            })
          });

          if (!updateVideoResponse.ok) {
            console.error('[BundleManager] Failed to update video item with product info:', updateVideoResponse.status);
          } else {
            console.log('[BundleManager] Successfully updated video item with product info');
          }
        }
      } else {
        console.warn('[BundleManager] Could not find main product to link');
      }

      // Wait for Shopify Functions to process
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Verify final cart state
      const finalCart = await fetch('/cart.js').then(r => r.json());
      const finalBundleItems = finalCart.items.filter(item =>
        item.properties?.productLinkId === productLinkId &&
        item.properties?._bundleComponent === 'true'
      );

      // Logging removed
      // Logging removed
      // Logging removed
      // Logging removed
      // Logging removed

      return { success: true, tier: tierInfo.current };

    } catch (error) {
      console.error('[BundleManager] Error syncing bundle:', error);
      if (syncPhase === 'adding') {
        await this.rollbackBundleSync(productLinkId, removedItems);
      }
      throw error;
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Show recalculating overlay
   */
  showRecalculatingOverlay() {
    // Remove any existing overlay
    this.hideRecalculatingOverlay();

    const overlay = document.createElement('div');
    overlay.id = 'bundle-recalculating-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeIn 0.2s ease-in;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      border-radius: 0.5rem;
      padding: 2rem;
      max-width: 20rem;
      text-align: center;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    `;

    content.innerHTML = `
      <div style="margin-bottom: 1rem;">
        <svg style="width: 3rem; height: 3rem; margin: 0 auto; animation: spin 1s linear infinite;" viewBox="0 0 24 24">
          <circle style="opacity: 0.25;" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
          <path style="opacity: 0.75;" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <h3 style="font-size: 1.125rem; font-weight: 700; margin-bottom: 0.5rem;">Recalculating Discounts...</h3>
      <p style="font-size: 0.875rem; color: #6b7280;">Please wait while we update your bundle pricing</p>
    `;

    // Add CSS animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);

    overlay.appendChild(content);
    document.body.appendChild(overlay);
  }

  /**
   * Hide recalculating overlay
   */
  hideRecalculatingOverlay() {
    const overlay = document.getElementById('bundle-recalculating-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  /**
   * Check if parent product has stock available
   * Utility method for defensive stock validation throughout the system
   * @param {string} productHandle - Parent product handle
   * @returns {Promise<{available: boolean, message: string, productData: object|null}>}
   */
  async checkParentProductStock(productHandle) {
    if (!productHandle) {
      console.warn('[BundleManager] No product handle provided for stock check');
      return {
        available: true, // Don't block if no handle provided
        message: 'No product handle provided',
        productData: null
      };
    }

    try {
      // Logging removed

      // Fetch parent product data from Shopify API
      const response = await fetch(`/products/${productHandle}.js`);

      if (!response.ok) {
        console.error(`[BundleManager] Failed to fetch product ${productHandle}: ${response.status}`);
        return {
          available: false,
          message: `Unable to verify product stock (${response.status})`,
          productData: null
        };
      }

      const productData = await response.json();
      const { variants, title } = productData;

      // Check if ANY variant has stock available
      const hasStockInAnyVariant = variants.some(variant => {
        // Check if variant is available
        if (variant.available === true) {
          return true;
        }

        // Check inventory policy - if 'continue', can sell when out of stock
        if (variant.inventory_policy === 'continue') {
          return true;
        }

        // Check if inventory management is disabled
        if (!variant.inventory_management || variant.inventory_management === null) {
          return true;
        }

        // Check if variant has inventory quantity > 0
        if (variant.inventory_quantity && variant.inventory_quantity > 0) {
          return true;
        }

        return false;
      });

      if (hasStockInAnyVariant) {
        // Logging removed
        return {
          available: true,
          message: `Parent product "${title}" has stock available`,
          productData
        };
      } else {
        // Logging removed
        return {
          available: false,
          message: `Parent product "${title}" is out of stock`,
          productData
        };
      }

    } catch (error) {
      console.error(`[BundleManager] Error checking parent stock for ${productHandle}:`, error);
      return {
        available: false,
        message: 'Error checking parent product stock',
        productData: null
      };
    }
  }

  /**
   * Validate cart on page load - check for orphaned addons
   */
  async validateCartOnLoad() {
    // Wait for DOM to be ready
    await new Promise(resolve => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', resolve);
      } else {
        resolve();
      }
    });

    // Perform validation after a short delay to ensure cart is loaded
    setTimeout(() => this.performCartValidation(), 1000);
  }

  /**
   * Perform cart validation - detect and cleanup orphaned addons
   */
  async performCartValidation() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());

      if (!cart.items || cart.items.length === 0) {
        return; // Empty cart, nothing to validate
      }

      // Find all unique productLinkIds
      const linkIds = new Set();
      cart.items.forEach(item => {
        if (item.properties?.productLinkId) {
          linkIds.add(item.properties.productLinkId);
        }
      });

      // For each linkId, check if parent exists
      for (const linkId of linkIds) {
        const parent = cart.items.find(item =>
          item.properties?.productLinkId === linkId &&
          !item.properties?._bundleComponent
        );

        const addons = cart.items.filter(item =>
          item.properties?.productLinkId === linkId &&
          item.properties?._bundleComponent === 'true'
        );

        // If addons exist without parent → orphaned
        if (!parent && addons.length > 0) {
          console.warn('[BundleManager] ⚠️  Orphaned addons detected on cart load:', linkId);
          console.warn('[BundleManager] Found', addons.length, 'orphaned addons, cleaning up...');
          await this.cleanupOrphanedAddons(linkId);
        }
      }
    } catch (error) {
      console.error('[BundleManager] Error during cart validation:', error);
    }
  }

  /**
   * Cleanup orphaned addons - remove addons without parent product
   */
  async cleanupOrphanedAddons(productLinkId) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());

      const orphanedAddons = cart.items.filter(item =>
        item.properties?.productLinkId === productLinkId &&
        item.properties?._bundleComponent === 'true'
      );

      if (orphanedAddons.length === 0) {
        console.log('[BundleManager] No orphaned addons found for cleanup');
        return;
      }

      console.log('[BundleManager] Cleaning up', orphanedAddons.length, 'orphaned addons');

      // Remove orphaned addons using bulk update
      const updates = {};
      orphanedAddons.forEach(addon => {
        updates[addon.key] = 0;
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        console.error('[BundleManager] Failed to cleanup orphaned addons');
        return;
      }

      // Refresh cart UI
      const updatedCart = await response.json();
      if (window.theme && window.theme.pubsub) {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: updatedCart,
          source: 'bundle-manager-cleanup'
        });
      }

      console.log('[BundleManager] ✓ Successfully cleaned up', orphanedAddons.length, 'orphaned addons');
    } catch (error) {
      console.error('[BundleManager] Error during orphaned addon cleanup:', error);
    }
  }
  }; // End of BundleManager class

  // Create global instance
  window.bundleManager = new window.BundleManager();
  // Logging removed
} else {
  // Logging removed
}
